<template>
  <div class="cancel-booking min-w-[500px] mx-auto">
    <!-- Loading State -->
    <div v-if="state === 'loading'" class="text-center py-8">
      <h2 class="text-xl font-bold mb-4">Processing your cancellation request...</h2>
      <p class="mb-4">Please wait while we process your request.</p>
      <div class="flex justify-center">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue"></div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="state === 'error'" class="py-2">
      <div
          class="font-title px-3 mb-4 pt-4 pb-3 bg-red-500 border border-black text-[34px] leading-none uppercase text-white">
        Error
      </div>
      <p class="mb-6">{{ errorMessage }}</p>
      <div class="flex justify-end">
        <!--        <button-->
        <!--          @click="retryCancel"-->
        <!--          class="bg-blue/90 hover:bg-blue font-title uppercase text-[24px] text-black border border-black px-4 py-2.5 cursor-pointer"-->
        <!--        >-->
        <!--          Try Again-->
        <!--        </button>-->
      </div>
    </div>

    <!-- Success State -->
    <div v-else-if="state === 'success'" class="py-2">
      <div class="font-title px-3 mb-4 pt-4 pb-3 bg-yellow border border-black text-[34px] leading-none uppercase">
        Booking Cancelled
      </div>
      <p class="mb-6">Your booking has been successfully cancelled.</p>
      <div class="mb-6 text-left" v-if="bookingDetails">
        <p class="mb-2"><span class="font-semibold">Date:</span> {{ bookingDetails.date }}</p>
        <p class="mb-2"><span class="font-semibold">Time:</span> {{ bookingDetails.time }}</p>
        <p class="mb-2"><span class="font-semibold">Name:</span> {{ bookingDetails.name }}</p>
        <p class="mb-2"><span class="font-semibold">Email:</span> {{ bookingDetails.email }}</p>
        <p><span class="font-semibold">Group Size:</span> {{ bookingDetails.groupSize }}</p>
      </div>
      <div class="flex justify-end">
        <a
            href="/"
            class="bg-blue/90 hover:bg-blue font-title uppercase text-[24px] text-black border border-black px-4 py-2.5 cursor-pointer no-underline"
        >
          Return Home
        </a>
      </div>
    </div>

    <!-- Initial State -->
    <div v-else class="py-2">
      <div class="font-title px-3 mb-4 pt-4 pb-3 bg-yellow border border-black text-[34px] leading-none uppercase">
        Cancel Booking
      </div>

      <div>
        <div v-if="bookingDetails.isDeleted">
          <p class="mb-4">This booking has been cancelled.</p>
        </div>
        <div v-else>
          <p class="mb-4">Are you sure you want to cancel the following booking?</p>
        </div>
        <div class="mb-6 text-left p-4 bg-gray-100 border border-gray-300 rounded" v-if="bookingDetails">
          <p class="mb-2"><span class="font-semibold">Date:</span> {{ bookingDetails.date }}</p>
          <p class="mb-2"><span class="font-semibold">Time:</span> {{ bookingDetails.time }}</p>
          <p class="mb-2"><span class="font-semibold">Name:</span> {{ bookingDetails.name }}</p>
          <p class="mb-2"><span class="font-semibold">Email:</span> {{ bookingDetails.email }}</p>
          <p><span class="font-semibold">Group Size:</span> {{ bookingDetails.groupSize }}</p>
        </div>

        <div class="flex justify-end space-x-3">
          <!--        <a-->
          <!--          href="/"-->
          <!--          class="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-100 no-underline"-->
          <!--        >-->
          <!--          Go Back-->
          <!--        </a>-->
          <button
              v-if="!bookingDetails.isDeleted"
              @click="cancelBooking"
              class="bg-blue/90 hover:bg-blue font-title uppercase text-[24px] text-black border border-black px-4 py-2.5 cursor-pointer"
          >
            Confirm Cancellation
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

// Props
const props = defineProps({
  bookingId: {
    type: String,
    default: ''
  },
  cancelKey: {
    type: String,
    default: ''
  }
});

// State management
const state = ref('loading'); // 'loading', 'initial', 'success', 'error'
const errorMessage = ref('');
const bookingDetails = ref(null);

// Cancel booking function
function cancelBooking() {
  state.value = 'loading';

  // Prepare data for the request
  const data = {
    bookingId: props.bookingId,
    cancelKey: props.cancelKey
  };

  // Get CSRF token
  const csrfToken = getCsrfToken();

  // Add CSRF token to data if global variables are defined
  if (typeof csrfTokenName !== 'undefined' && typeof csrfTokenValue !== 'undefined') {
    data[csrfTokenName] = csrfTokenValue;
  }

  // Make the request to cancel the booking
  fetch('/actions/museum/booking/cancel', {
    method: 'POST',
    headers: {
      'Content-type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken
    },
    body: JSON.stringify(data)
  })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        if (data.success) {
          state.value = 'success';
          bookingDetails.value = data.booking;
        } else {
          state.value = 'error';
          errorMessage.value = data.error || 'An error occurred while cancelling your booking.';
        }
      })
      .catch(error => {
        state.value = 'error';
        errorMessage.value = error.message || 'An error occurred while cancelling your booking.';
      });
}

// Retry cancellation
function retryCancel() {
  state.value = 'initial';
}

// Get CSRF token from meta tag
function getCsrfToken() {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  return csrfToken || '';
}

// Fetch booking details
function fetchBookingDetails() {
  if (!props.bookingId || !props.cancelKey) {
    state.value = 'error';
    errorMessage.value = 'Invalid booking information. Please check your cancellation link.';
    return;
  }

  // Make the request to get booking details
  fetch(`/actions/museum/booking/get-booking-details?bookingId=${props.bookingId}&cancelKey=${props.cancelKey}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        if (data.success) {
          bookingDetails.value = data.booking;
          state.value = 'initial';
        } else {
          state.value = 'error';
          errorMessage.value = data.error || 'An error occurred while retrieving your booking details.';
        }
      })
      .catch(error => {
        state.value = 'error';
        errorMessage.value = error.message || 'An error occurred while retrieving your booking details.';
      });
}

// Check if we have both bookingId and cancelKey on mount and fetch booking details
onMounted(() => {
  fetchBookingDetails();
});
</script>
